import { getRequestCommunityDomain } from "utils";
import { getToken, resetToken } from "../services/auth";
import { getMyIp } from "../services/ip";
const axios = require('axios');

// Define the real functions first
const environment = process.env.NODE_ENV || "development";
const isProd = environment === "production";

const realDefaultHeaders = async () => {
    return {
        "Content-Type": "application/json;charset=UTF-8",
        "Public-Ip": await getMyIp(),
        "Community": getRequestCommunityDomain()
    }
}

const realGetAuthorizationHeader = () => {
    const token = getToken();
    return Boolean(token) ? ({
        "Authorization": `Bearer ${getToken()}`,
    }) : {};
}

const realParseError = (error) => {
    let parsedError = {
        code: 1000,
        msg: ""
    }
    if (error && error.response && error.response.data) {
        let msg = error.response.data.message || error.response.data.error;
        if (typeof msg === "string") parsedError.msg = msg.trim().replace('[', '').replace(']', '');
        let code = error.response.data.code || error.response.data.status;
        if (code) parsedError.code = code;
    }
    // OTP screen must handle invalid one-time code locally (show inline error instead of global redirect).
    const requestUrl = error?.config?.url || "";
    const isOtpValidateRequest = requestUrl.includes('/api/auth/otp/validate');
    // Check specifically for 401 Unauthorized which might be mapped to 405 by backend/proxy?
    if ((parsedError.code === 405 || error.response?.status === 401) && !isOtpValidateRequest) {
        console.warn("API Helper: Received 401/405, resetting token and redirecting to login.");
        resetToken();
        // Use window.location for redirection in browser environment
        if (typeof window !== 'undefined') {
            window.location.assign('/login');
        }
    }

    if (parsedError.code === 503 || error.message === 'ERR:TIMEOUT' || error.message === 'ERR_FAILED' || error.message === 'Network Error' || error.response?.status === 0) {
        realDispatchErrorEvent('error', 'Server is unavailable');
    }

    realSendToSentry(error);

    // Ensure the rejected promise includes the response for status checks elsewhere
    parsedError.response = error.response;
    return parsedError;
}

function realDispatchErrorEvent(eventName, message) {
    // Check if running in a browser environment
    if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
        const errorEvent = new CustomEvent(eventName, {
            detail: { message }
        });
        window.dispatchEvent(errorEvent);
    } else {
        console.error(`Cannot dispatch event '${eventName}': Not in a browser environment.`);
    }
}

async function realSendToSentry(error) {
    if (!isProd) return;
    try {
        const Sentry = await import("@sentry/react");
        Sentry.captureException(error);
    } catch (e) {
        console.error('Sentry import or capture failed:', e);
    }
}

const realGet = async (url, params, headers) => {
    return axios({
        method: 'get',
        url: url,
        params: params,
        headers: Object.assign({}, await realDefaultHeaders(), realGetAuthorizationHeader(), headers),
    })
        // Return the whole response data, not just r.data.data
        .then(r => r.data)
        .catch(e => Promise.reject(realParseError(e)))
}

const realPost = async (url, params, body, headers) => {
    const defaultHeaders = await realDefaultHeaders();
    const authHeaders = realGetAuthorizationHeader();
    const mergedHeaders = Object.assign({}, defaultHeaders, authHeaders, headers);
    
    // Log if Telegram pending token header is present
    if (mergedHeaders['X-Telegram-Pending-Token']) {
        console.log('[ApiHelper] 🔗 Sending X-Telegram-Pending-Token header:', mergedHeaders['X-Telegram-Pending-Token'].substring(0, 30) + '...');
    }
    
    return axios({
        method: 'post',
        url: url,
        params: params,
        data: body,
        headers: mergedHeaders,
    })
        .then(r => r.data)
        .catch(e => Promise.reject(realParseError(e)))
}

const realPatch = async (url, params, body, headers) => {
    return axios({
        method: 'patch',
        url: url,
        params: params,
        data: body,
        headers: Object.assign({}, await realDefaultHeaders(), realGetAuthorizationHeader(), headers),
    })
        .then(r => r.data)
        .catch(e => Promise.reject(realParseError(e)))
}

const realPut = async (url, params, body, headers) => {
    return axios({
        method: 'put',
        url: url,
        params: params,
        data: body,
        headers: Object.assign({}, await realDefaultHeaders(), realGetAuthorizationHeader(), headers),
    })
        .then(r => r.data)
        .catch(e => Promise.reject(realParseError(e)))
}

const realDel = async (url, params, body, headers) => {
    return axios({
        method: 'delete',
        url: url,
        params: params,
        data: body,
        headers: Object.assign({}, await realDefaultHeaders(), realGetAuthorizationHeader(), headers),
    })
        .then(r => r.data)
        .catch(e => Promise.reject(realParseError(e)))
}

const realSseEventSource = async (url) => {
    // Check if running in a browser environment
    if (typeof window !== 'undefined' && typeof EventSource !== 'undefined') {
        return new EventSource(url, {
            withCredentials: true
        });
    } else {
        console.error("Cannot create EventSource: Not in a browser environment.");
        // Return a dummy object or throw an error depending on desired behavior
        return { close: () => { } };
    }
}

let get, post, patch, put, del, sseEventSource;

if (process.env.REACT_APP_USE_MOCKS === 'true') {
    console.log("--- USING EVENT MOCKS ---");

    const MOCK_EVENT_1 = {
        communityId: "comm-123",
        createdBy: "user-abc",
        description: "This is a fantastic mock event description. Join us for fun and networking!",
        eventDateTime: "2025-06-15T19:00:00",
        googleMapsLink: "https://maps.google.com",
        id: "event-1",
        imageUrl: "https://via.placeholder.com/600x400?text=Mock+Event+1",
        location: "Mock City Conference Hall",
        maxParticipants: 50,
        organizerContact: "Mock Organizer",
        paid: false,
        price: 0,
        title: "Mock Event Alpha",
        visibility: "PUBLIC"
    };

    const MOCK_EVENT_2 = {
        communityId: "comm-123",
        createdBy: "user-xyz",
        description: "Another great event focused on tech talks.",
        eventDateTime: "2025-07-20T18:30:00",
        googleMapsLink: null,
        id: "event-2",
        imageUrl: "https://via.placeholder.com/600x400?text=Mock+Event+2",
        location: "Online",
        maxParticipants: 100,
        organizerContact: "Tech Lead",
        paid: true,
        price: 10,
        title: "Mock Tech Meetup",
        visibility: "PUBLIC"
    };

    // Keep only event-related mock data
    const MOCK_PAST_EVENT = {
        ...MOCK_EVENT_1,
        id: "event-past-1",
        title: "Mock Past Event",
        eventDateTime: "2024-01-10T19:00:00",
    };

    const MOCK_CONFIRMED_PARTICIPANTS = {
        hasMore: false,
        list: [
            { id: "user-1", firstName: "Alice", lastName: "Confirmed", avatarUrl: "https://via.placeholder.com/40?text=AC" },
            { id: "user-2", firstName: "Bob", lastName: "Attending", avatarUrl: "https://via.placeholder.com/40?text=BA" },
            { id: "user-3", firstName: "Charlie", lastName: "Confirmed", avatarUrl: "https://via.placeholder.com/40?text=AC" },
            { id: "user-4", firstName: "Dave", lastName: "Attending", avatarUrl: "https://via.placeholder.com/40?text=BA" },
            { id: "user-5", firstName: "Eve", lastName: "Confirmed", avatarUrl: "https://via.placeholder.com/40?text=AC" },
            { id: "user-6", firstName: "Frank", lastName: "Attending", avatarUrl: "https://via.placeholder.com/40?text=BA" },
            { id: "user-7", firstName: "George", lastName: "Confirmed", avatarUrl: "https://via.placeholder.com/40?text=AC" },
            { id: "user-8", firstName: "Henry", lastName: "Attending", avatarUrl: "https://via.placeholder.com/40?text=BA" },
            { id: "user-9", firstName: "Ivy", lastName: "Confirmed", avatarUrl: "https://via.placeholder.com/40?text=AC" },
            { id: "user-10", firstName: "John", lastName: "Attending", avatarUrl: "https://via.placeholder.com/40?text=BA" },
            { id: "user-11", firstName: "Kyle", lastName: "Confirmed", avatarUrl: "https://via.placeholder.com/40?text=AC" },
            { id: "user-12", firstName: "Liam", lastName: "Attending", avatarUrl: "https://via.placeholder.com/40?text=BA" },
        ],
        page: 0
    };

    // Store participation status for testing join/cancel
    let participationStatusStore = {
        'event-1': 'NOT_PARTICIPATING', // Initial status for event-1
        'event-2': 'CONFIRMED', // Initial status for event-2
        'event-past-1': 'CANCELLED',  // Initial status for past event
    };

    // Use jest.fn() if Jest is available globally in your dev setup, otherwise use simple functions
    const mockFn = typeof jest !== 'undefined' ? jest.fn : (fn) => fn;

    get = mockFn(async (url, params) => {
        console.log(`[MOCK GET] URL: ${url}`, params);
        await new Promise(resolve => setTimeout(resolve, 150)); // Simulate network delay

        // Note: VoleeApi calls voleeApi.event.upcoming/past which uses realGet internally.
        // Mocking the URL here is a fallback if apiHelper.get is called directly with these URLs.
        if (url.includes('/api/event/upcoming')) {
            return Promise.resolve({ data: [MOCK_EVENT_1, MOCK_EVENT_2] });
        }
        if (url.includes('/api/event/past')) {
            return Promise.resolve({ data: [MOCK_PAST_EVENT] });
        }

        const eventDetailMatch = url.match(/\/api\/event\/([a-zA-Z0-9-]+)$/);
        if (eventDetailMatch) {
            const eventId = eventDetailMatch[1];
            if (eventId === MOCK_EVENT_1.id) return Promise.resolve({ data: MOCK_EVENT_1 });
            if (eventId === MOCK_EVENT_2.id) return Promise.resolve({ data: MOCK_EVENT_2 });
            if (eventId === MOCK_PAST_EVENT.id) return Promise.resolve({ data: MOCK_PAST_EVENT });
            return Promise.reject({ code: 404, msg: 'Event not found', response: { status: 404 } });
        }

        const confirmedParticipantsMatch = url.match(/\/api\/event\/([a-zA-Z0-9-]+)\/participants\/confirmed$/);
        if (confirmedParticipantsMatch) {
            return Promise.resolve({ data: MOCK_CONFIRMED_PARTICIPANTS });
        }

        const participantCountMatch = url.match(/\/api\/event\/([a-zA-Z0-9-]+)\/participants\/count$/);
        if (participantCountMatch) {
            const eventId = participantCountMatch[1];
            if (eventId === MOCK_EVENT_1.id || eventId === MOCK_EVENT_2.id || eventId === MOCK_PAST_EVENT.id) {
                return Promise.resolve({ data: MOCK_CONFIRMED_PARTICIPANTS.list.length });
            }
            return Promise.resolve({ data: 0 });
        }

        const participationStatusMatch = url.match(/\/api\/event\/participations\/([a-zA-Z0-9-]+)\/([a-zA-Z0-9-]+)$/);
        if (participationStatusMatch) {
            const userId = participationStatusMatch[1];
            const eventId = participationStatusMatch[2];
            const status = participationStatusStore[eventId];
            console.log(`[MOCK GET Status] User: ${userId}, Event: ${eventId}, Status: ${status}`);
            if (status && status !== 'NOT_PARTICIPATING') {
                return Promise.resolve({ data: { eventId, participantId: userId, status } });
            } else {
                return Promise.reject({ code: 404, msg: 'Participation not found', response: { status: 404 } });
            }
        }

        console.log(`[MOCK GET] Passing non-event URL to real implementation: ${url}`);
        return realGet(url, params); // Call the original function
    });

    post = mockFn(async (url, params, body) => {
        console.log(`[MOCK POST] URL: ${url}`, params, body);
        await new Promise(resolve => setTimeout(resolve, 150)); // Simulate network delay

        const confirmParticipationMatch = url.match(/\/api\/event\/([a-zA-Z0-9-]+)\/confirm-participation$/);
        if (confirmParticipationMatch) {
            const eventId = confirmParticipationMatch[1];
            participationStatusStore[eventId] = 'CONFIRMED'; // Update mock status
            console.log(`[MOCK POST Confirm] Event: ${eventId}, New Status: CONFIRMED`);
            return Promise.resolve({ status: 'OK' }); // Simulate success
        }

        console.log(`[MOCK POST] Passing non-event URL to real implementation: ${url}`);
        return realPost(url, params, body); // Call the original function
    });

    patch = mockFn(async (url, params, body) => {
        console.log(`[MOCK PATCH] URL: ${url}`, params, body);
        await new Promise(resolve => setTimeout(resolve, 150)); // Simulate network delay

        const cancelParticipationMatch = url.match(/\/api\/event\/([a-zA-Z0-9-]+)\/cancel-participation$/);
        if (cancelParticipationMatch) {
            const eventId = cancelParticipationMatch[1];
            participationStatusStore[eventId] = 'CANCELLED'; // Update mock status
            console.log(`[MOCK PATCH Cancel] Event: ${eventId}, New Status: CANCELLED`);
            return Promise.resolve({ status: 'OK' }); // Simulate success
        }

        console.log(`[MOCK PATCH] Passing non-event URL to real implementation: ${url}`);
        return realPatch(url, params, body); // Call the original function
    });

    put = mockFn(async (url, params, body) => {
        console.log(`[MOCK PUT] URL: ${url}`, params, body);
        await new Promise(resolve => setTimeout(resolve, 150)); // Simulate network delay
        // Add specific event PUT mocks if needed, otherwise fallback
        console.log(`[MOCK PUT] Passing non-event URL to real implementation: ${url}`);
        return realPut(url, params, body); // Call the original function
    });

    del = mockFn(async (url, params, body) => {
        console.log(`[MOCK DELETE] URL: ${url}`, params, body);
        await new Promise(resolve => setTimeout(resolve, 150)); // Simulate network delay
        // Add specific event delete mocks if needed, otherwise fallback
        console.log(`[MOCK DELETE] Passing non-event URL to real implementation: ${url}`);
        return realDel(url, params, body); // Call the original function
    });

    sseEventSource = mockFn(async (url) => {
        console.log(`[MOCK SSE] URL: ${url}`);
        // Add specific event SSE mocks if needed, otherwise fallback
        console.log(`[MOCK SSE] Passing non-event URL to real implementation: ${url}`);
        return realSseEventSource(url); // Call the original function
    });

} else {
    get = realGet;
    post = realPost;
    patch = realPatch;
    put = realPut;
    del = realDel;
    sseEventSource = realSseEventSource;
}

// Export the assigned functions (either real or mock wrappers)
export { get, post, patch, put, del, sseEventSource };
